'use client';

import { UIMarket, UIOrderType } from '@velocity-exchange/common';
import useOraclePrice from '../useOraclePrice';
import {
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import {
	getMessageInfoForOrderViolation,
	getPriceInputLabel,
	getShouldShowSecondaryPriceBox,
} from 'src/utils/trade';
import { useMarketStepSize } from '../useMarketStepSize';
import { ZERO_QUOTE_BIGNUM } from 'src/constants/math';
import { usePerpMarketsLookup } from '../perpMarkets/usePerpMarketsLookup';

import usePreviewOrder from 'src/components/TradeForm/hooks/usePreviewOrder';
import { useMarketPriceState } from 'src/hooks/query/orderbook/useMarketPriceState';

type ClosePositionStateEngineProps = {
	market: number;
	selectedOrderType: UIOrderType;
	priceBoxValue: BigNum;
	secondaryPriceBoxValue: BigNum;
	currentPositionDirection: 'long' | 'short';
	baseAmountOfClose: BigNum;
};

export function getQuotePriceToUse(
	selectedOrderType: UIOrderType,
	orderDirection: 'long' | 'short' | 'buy' | 'sell',
	marketPrice: BigNum,
	currentOraclePrice: BigNum,
	limitOrTriggerPrice: BigNum,
	triggerMarketImmediateExecutionPrice: BigNum,
	triggerLimitPrice: BigNum
) {
	let priceToUse: BigNum;

	const isBuyOrder = orderDirection === 'long' || orderDirection === 'buy';
	const isSellOrder = orderDirection === 'short' || orderDirection === 'sell';

	switch (selectedOrderType) {
		case 'market':
			priceToUse = marketPrice;
			break;
		case 'limit':
			priceToUse = limitOrTriggerPrice;

			// long position - short close - current market price above limit price will lead to immediate execution of market order
			if (isSellOrder && marketPrice.gte(limitOrTriggerPrice)) {
				priceToUse = marketPrice;
			}

			// short position - long close - current market price below limit price will lead to immediate execution of market order
			if (isBuyOrder && marketPrice.lte(limitOrTriggerPrice)) {
				priceToUse = marketPrice;
			}

			break;
		case 'stopMarket':
			priceToUse = limitOrTriggerPrice;

			// short position stop loss - current oracle price above trigger price will lead to immediate execution of market order
			if (isBuyOrder && currentOraclePrice.gte(limitOrTriggerPrice)) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}

			// long position stop loss - current oracle price below trigger price will lead to immediate execution of market order
			if (isSellOrder && currentOraclePrice.lte(limitOrTriggerPrice)) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}

			break;
		case 'takeProfitMarket':
			priceToUse = limitOrTriggerPrice;

			// short position - take profit means price below entry - current oracle price below trigger price will lead to immediate execution of market order
			if (isBuyOrder && currentOraclePrice.lte(limitOrTriggerPrice)) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}

			// long position - take profit means price above entry - current oracle price above trigger price will lead to immediate execution of market order
			if (isSellOrder && currentOraclePrice.gte(limitOrTriggerPrice)) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}
			break;
		case 'stopLimit':
			priceToUse = triggerLimitPrice;

			// short position stop loss limit
			// - current oracle price above trigger price will lead to immediate placement of limit order (doesn't affect pnl)
			// - long close order - current market price below limit order will lead to immediate execution of market order
			if (
				isBuyOrder &&
				currentOraclePrice.gte(limitOrTriggerPrice) &&
				marketPrice.lte(limitOrTriggerPrice)
			) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}

			// long position stop loss limit
			// - current oracle price below trigger price will lead to immediate execution of market order (doesn't affect pnl)
			// - short close limit order - current market above below limit order will lead to immediate execution of market order
			if (
				isSellOrder &&
				currentOraclePrice.lte(limitOrTriggerPrice) &&
				marketPrice.gte(limitOrTriggerPrice)
			) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}
			break;
		case 'takeProfitLimit':
			priceToUse = triggerLimitPrice;

			// short position take profit limit
			// - current oracle price below trigger price will lead to immediate execution of market order (doesn't affect pnl)
			// - long close limit order - current market price below limit order will lead to immediate execution of market order
			if (
				isBuyOrder &&
				currentOraclePrice.lte(limitOrTriggerPrice) &&
				marketPrice.lte(triggerLimitPrice)
			) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}

			// long position take profit limit
			// - current oracle price above trigger price will lead to immediate execution of market order  (doesn't affect pnl)
			// - short close limit order - current market price above limit order will lead to immediate execution of market order
			if (
				isSellOrder &&
				currentOraclePrice.gte(limitOrTriggerPrice) &&
				marketPrice.gte(triggerLimitPrice)
			) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}
			break;
		case 'oracle':
			priceToUse = marketPrice;
			break;
		case 'oracleLimit':
			priceToUse = limitOrTriggerPrice.add(currentOraclePrice);
			break;
		default:
			console.warn(
				'Unhandled order type in Close Position Popup',
				selectedOrderType
			);
			return BigNum.zero(PRICE_PRECISION_EXP);
	}

	return priceToUse;
}

const useClosePositionStateEngine = ({
	market,
	selectedOrderType,
	priceBoxValue,
	secondaryPriceBoxValue,
	currentPositionDirection,
	baseAmountOfClose,
}: ClosePositionStateEngineProps) => {
	const uiMarket = UIMarket.createPerpMarket(market);
	const marketId = uiMarket.marketId;
	const perpMarketsLookup = usePerpMarketsLookup();
	const marketSymbol = perpMarketsLookup[marketId.marketIndex].symbol;
	const closePositionOrderDirection =
		currentPositionDirection === 'long' ? 'short' : 'long';

	const stepSizeNum = useMarketStepSize(marketId);
	const closeSizeNum = baseAmountOfClose.toNum();

	// App Utility Hooks
	const currentOraclePrice = useOraclePrice(marketId);

	// Use usePreviewOrder hook to get price impact info
	const previewOrderParams = usePreviewOrder(marketId);
	const priceImpactInfo = previewOrderParams.priceImpact;
	const currentMarkPrice = BigNum.from(
		priceImpactInfo?.markPrice.toNumber() ?? 0,
		QUOTE_PRECISION_EXP
	);

	const quoteSizeOfClose = getQuoteAmountOfClose().abs();

	const priceInputLabel = getPriceInputLabel(selectedOrderType);

	const { data: marketPriceState } = useMarketPriceState(marketId);

	// Show Secondary Price Box
	const showSecondaryPriceBox =
		getShouldShowSecondaryPriceBox(selectedOrderType);

	const errorMessageInfo = getMessageInfoForOrderViolation(
		selectedOrderType,
		priceBoxValue.toNum(),
		currentMarkPrice.toNum() ?? 0,
		false,
		secondaryPriceBoxValue.toNum(),
		closePositionOrderDirection,
		currentOraclePrice?.toNum() ?? 0,
		marketSymbol,
		closeSizeNum,
		stepSizeNum,
		'closePosition',
		undefined,
		marketPriceState
	);

	function getQuoteAmountOfClose() {
		const priceToUse = getQuotePriceToUse(
			selectedOrderType,
			closePositionOrderDirection,
			currentMarkPrice,
			currentOraclePrice ?? ZERO_QUOTE_BIGNUM,
			priceBoxValue,
			currentMarkPrice,
			secondaryPriceBoxValue
		);

		const closeAmountQuoteValue = priceToUse
			.mul(baseAmountOfClose)
			.shiftTo(PRICE_PRECISION_EXP);

		return closeAmountQuoteValue.val;
	}

	return {
		priceBoxHeading: priceInputLabel,
		showSecondaryPriceBox,
		errorMessageInfo,
		quoteSizeOfClose,
		priceImpactInfo,
	};
};

export default useClosePositionStateEngine;
